import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/lib/components/Dialog";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  IconChevronLeft,
  IconMusic,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { Button } from "@/lib/components/Button/Button";
import TrackListView from "./TrackListView";
import AddOrEditTrackForm from "./AddOrEditTrackForm";
import { AnimatePresence, motion } from "motion/react";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  BackgroundMusicDto,
  BackgroundMusicOwnershipFilterDto,
} from "@repo/api/background-music";
import { BackgroundMusicDialogTab, Tabs } from "./Tabs";
import { Filters } from "./Filters";
import {
  useBackgroundMusicMetadataQuery,
  usePagedBackgroundMusicsQuery,
} from "@/lib/queries/background-music-queries";

const SEARCH_DEBOUNCE_MS = 300;

function BackgroundMusicDialog() {
  const currentView = useEditorStore(
    (state) => state.backgroundMusicDialogState,
  );
  const setBackgroundMusicView = useEditorStore(
    (state) => state.service.setBackgroundMusicView,
  );
  const compositionBackgroundMusic = useEditorStore(
    (state) => state.video?.compositionJson.backgroundMusic,
  );
  const updateVideo = useEditorStore((state) => state.service.updateVideo);
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const videoIsPlaying = useEditorStore(
    (state) => state.videoPreviewState.isPlaying,
  );

  const selectedExternalId = compositionBackgroundMusic?.externalId;
  const selectedMusic = useEditorStore((state) =>
    selectedExternalId ? state.backgroundMusics[selectedExternalId] : undefined,
  );

  const [activeTab, setActiveTab] =
    useState<BackgroundMusicDialogTab>("ALL_TRACKS");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGenres, setSelectedGenres] = useState<Set<string>>(new Set());
  const [selectedMoods, setSelectedMoods] = useState<Set<string>>(new Set());
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [ownership, setOwnership] =
    useState<BackgroundMusicOwnershipFilterDto>("ALL");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const isDialogOpen = currentView.view !== "HIDDEN";

  const {
    data: pagedData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: isLoadingTracks,
  } = usePagedBackgroundMusicsQuery(
    teamExternalId,
    {
      search: debouncedSearch,
      ownership,
      genres: [...selectedGenres],
      moods: [...selectedMoods],
      tags: [...selectedTags],
      favoritesOnly: activeTab === "FAVORITES",
    },
    { enabled: isDialogOpen && activeTab !== "ADD_MUSIC" },
  );

  const { data: metadata } = useBackgroundMusicMetadataQuery(teamExternalId, {
    enabled: isDialogOpen,
  });

  const [previewTrack, setPreviewTrack] = useState<BackgroundMusicDto | null>(
    null,
  );
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [previewDurationMs, setPreviewDurationMs] = useState(0);
  const [pendingDroppedTrack, setPendingDroppedTrack] = useState<File | null>(
    null,
  );

  useEffect(() => {
    if (currentView.view === "FORM" && !currentView.editingTrackExternalId) {
      setActiveTab("ADD_MUSIC");
    } else if (currentView.view === "LIST") {
      setActiveTab((prev) => (prev === "ADD_MUSIC" ? "ALL_TRACKS" : prev));
    }
  }, [currentView.view, currentView.editingTrackExternalId]);

  useEffect(() => {
    if (currentView.view === "HIDDEN") {
      setPreviewTrack(null);
      setIsPreviewPlaying(false);
      setPendingDroppedTrack(null);
    }
  }, [currentView.view]);

  useEffect(() => {
    if (activeTab === "ADD_MUSIC") {
      setIsPreviewPlaying(false);
    }
  }, [activeTab]);

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const allGenres = metadata?.genres ?? [];
  const allMoods = metadata?.moods ?? [];
  const allTags = metadata?.tags ?? [];

  const genreItems = useMemo(
    () =>
      allGenres.map((label) => ({ label, checked: selectedGenres.has(label) })),
    [allGenres, selectedGenres],
  );

  const moodItems = useMemo(
    () =>
      allMoods.map((label) => ({ label, checked: selectedMoods.has(label) })),
    [allMoods, selectedMoods],
  );

  const tagItems = useMemo(
    () => allTags.map((label) => ({ label, checked: selectedTags.has(label) })),
    [allTags, selectedTags],
  );

  const toggleSelection = (
    setSelected: Dispatch<SetStateAction<Set<string>>>,
    label: string | undefined,
    checked: boolean,
  ) => {
    if (!label) {
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(label);
      } else {
        next.delete(label);
      }
      return next;
    });
  };

  const pagedTracks = useMemo(
    () => pagedData?.pages.flatMap((page) => page.backgroundMusics) ?? [],
    [pagedData],
  );

  useEffect(() => {
    const previewSrc = previewTrack?.src;
    if (!previewSrc || previewTrack?.durationMs) {
      setPreviewDurationMs(0);
      return;
    }

    const audio = new Audio(previewSrc);
    const handleLoadedMetadata = () => {
      setPreviewDurationMs(audio.duration * 1000);
    };
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [previewTrack?.src, previewTrack?.durationMs]);

  const handlePreviewPlayPause = (track: BackgroundMusicDto) => {
    if (previewTrack?.externalId === track.externalId) {
      setIsPreviewPlaying((prev) => !prev);
    } else {
      setPreviewTrack(track);
      setIsPreviewPlaying(true);
    }
  };

  const handleEditTrack = (externalId: string) => {
    setBackgroundMusicView("FORM", externalId);
  };

  const handleTabChange = (tab: BackgroundMusicDialogTab) => {
    setActiveTab(tab);
    if (tab === "ADD_MUSIC") {
      setBackgroundMusicView("FORM");
    } else {
      setBackgroundMusicView("LIST");
    }
  };

  const handleClearAllFilters = () => {
    setSelectedGenres(new Set());
    setSelectedMoods(new Set());
    setSelectedTags(new Set());
    setOwnership("ALL");
  };

  const hasActiveFilters =
    genreItems.some((i) => i.checked) ||
    moodItems.some((i) => i.checked) ||
    tagItems.some((i) => i.checked) ||
    ownership !== "ALL";

  const displayedTracks = useMemo(() => {
    const shouldPin =
      activeTab === "ALL_TRACKS" ||
      (activeTab === "FAVORITES" && selectedMusic?.isFavorite);
    if (!shouldPin || !selectedMusic || isLoadingTracks) {
      return pagedTracks;
    }
    // Pin the selected (possibly deleted) track to the top of the list.
    const rest = pagedTracks.filter(
      (track) => track.externalId !== selectedMusic.externalId,
    );
    return [selectedMusic, ...rest];
  }, [activeTab, pagedTracks, selectedMusic, isLoadingTracks]);

  const isEditing = !!currentView.editingTrackExternalId;

  const handleDropTrack = (files: File[]) => {
    const track = files[0];
    if (!track) return;
    setPendingDroppedTrack(track);
    if (!isEditing) {
      setBackgroundMusicView("FORM");
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: { "audio/*": [".mp3", ".wav", ".ogg"] },
    onDrop: handleDropTrack,
  });

  return (
    <Dialog
      open={currentView.view !== "HIDDEN"}
      onOpenChange={(open) => {
        setBackgroundMusicView(open ? "LIST" : "HIDDEN");
      }}
    >
      <div className="flex items-center gap-1">
        <DialogTrigger asChild>
          <Button
            asChild
            className="gap-0 [&_svg]:size-6"
            variant="outline"
            onClick={() => setBackgroundMusicView("LIST", undefined)}
          >
            <button className="flex h-36 w-80 flex-col items-center justify-center">
              {selectedMusic ? (
                <p className="mb-0.5 text-sm font-normal text-text-secondary">
                  Selected track
                </p>
              ) : (
                <IconMusic className="mb-2" />
              )}
              <span className="line-clamp-3 max-w-full px-4 text-center text-sm font-medium text-ellipsis whitespace-normal">
                {selectedMusic ? selectedMusic.name : "Select background music"}
              </span>
            </button>
          </Button>
        </DialogTrigger>
        {selectedMusic && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              updateVideo((video) => {
                video.compositionJson.backgroundMusic = undefined;
              });
            }}
            className="group w-9 p-2 text-text-secondary transition-colors duration-200 hover:text-black"
          >
            <IconX />
          </Button>
        )}
      </div>
      <DialogContent
        className="mx-auto h-[calc(100%-48px)] max-h-196 w-[calc(100%-48px)] max-w-273 border-none"
        classNameInner="flex flex-col gap-0 overflow-hidden p-0 sm:px-0"
        showCloseButton={false}
      >
        <div className="relative flex h-full w-full flex-col">
          <DialogHeader
            showCloseButton
            variant="bordered"
            className="min-h-18 px-8 pr-5"
          >
            <DialogTitle>Background music</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-0 flex-1">
            <aside className="flex w-71.25 flex-col border-r border-grey-200">
              <Tabs activeTab={activeTab} onTabChange={handleTabChange} />
              {activeTab !== "ADD_MUSIC" && (
                <Filters
                  ownership={ownership}
                  onOwnershipChange={setOwnership}
                  genreItems={genreItems}
                  moodItems={moodItems}
                  tagItems={tagItems}
                  onGenreChange={(index, checked) =>
                    toggleSelection(
                      setSelectedGenres,
                      allGenres[index],
                      checked,
                    )
                  }
                  onMoodChange={(index, checked) =>
                    toggleSelection(setSelectedMoods, allMoods[index], checked)
                  }
                  onTagChange={(index, checked) =>
                    toggleSelection(setSelectedTags, allTags[index], checked)
                  }
                  hasActiveFilters={hasActiveFilters}
                  onClearAll={handleClearAllFilters}
                />
              )}
            </aside>
            <div
              className="relative flex min-w-0 flex-1 flex-col overflow-hidden"
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              {activeTab !== "ADD_MUSIC" ? (
                <div className="relative flex flex-1 flex-col overflow-hidden">
                  <AnimatePresence mode="wait" initial={false}>
                    {isEditing ? (
                      <motion.div
                        key={`EDIT:${currentView.editingTrackExternalId}`}
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{
                          type: "tween",
                          duration: 0.15,
                          ease: "easeOut",
                        }}
                        className="absolute inset-0 flex flex-col overflow-y-auto"
                      >
                        <div className="mx-auto w-full max-w-lg px-8 pb-10">
                          <FormHeader
                            isEditing
                            onBack={() => setBackgroundMusicView("LIST")}
                          />
                          <AddOrEditTrackForm
                            initialTrack={pendingDroppedTrack}
                            onInitialTrackConsumed={() =>
                              setPendingDroppedTrack(null)
                            }
                          />
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="LIST"
                        initial={{ x: "-100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "-100%" }}
                        transition={{
                          type: "tween",
                          duration: 0.15,
                          ease: "easeOut",
                        }}
                        className="absolute inset-0 flex flex-col"
                      >
                        <TrackListView
                          tracks={displayedTracks}
                          searchQuery={searchTerm}
                          onSearchChange={setSearchTerm}
                          previewTrack={previewTrack}
                          isPreviewPlaying={isPreviewPlaying}
                          setIsPreviewPlaying={setIsPreviewPlaying}
                          onPreviewPlayPause={handlePreviewPlayPause}
                          onEditTrack={handleEditTrack}
                          previewDurationMs={
                            previewTrack?.durationMs || previewDurationMs
                          }
                          videoIsPlaying={videoIsPlaying}
                          isFavoritesTab={activeTab === "FAVORITES"}
                          showFavoritesEmptyState={activeTab === "FAVORITES"}
                          isLoading={isLoadingTracks}
                          hasNextPage={hasNextPage}
                          isFetchingNextPage={isFetchingNextPage}
                          onLoadMore={fetchNextPage}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="flex flex-1 flex-col overflow-y-auto">
                  <div className="mx-auto w-full max-w-lg px-8 pb-10">
                    <FormHeader
                      isEditing={false}
                      onBack={() => setBackgroundMusicView("LIST")}
                    />
                    <AddOrEditTrackForm
                      initialTrack={pendingDroppedTrack}
                      onInitialTrackConsumed={() =>
                        setPendingDroppedTrack(null)
                      }
                    />
                  </div>
                </div>
              )}
              {isDragActive && (
                <DropzoneOverlay
                  description={
                    isEditing
                      ? "to replace the current track"
                      : "to add it to your library"
                  }
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FormHeader({
  isEditing,
  onBack,
}: {
  isEditing: boolean;
  onBack: () => void;
}) {
  if (!isEditing) {
    return (
      <div className="mb-6 flex items-center gap-2 pt-8">
        <span className="flex flex-row items-center gap-2.5 text-base font-semibold text-text-primary">
          Add music
        </span>
      </div>
    );
  }

  return (
    <div className="mb-6 flex items-center gap-2 pt-8">
      <button
        type="button"
        onClick={onBack}
        className="flex cursor-pointer flex-row items-center gap-2.5 text-base font-semibold text-text-primary"
      >
        <IconChevronLeft
          className="shrink-0 text-text-primary"
          width={18}
          height={18}
          strokeWidth={2.5}
        />
        Edit music
      </button>
    </div>
  );
}

function DropzoneOverlay({ description }: { description: string }) {
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center rounded-br-2xl bg-primary-100/95">
      <div className="flex h-full w-full flex-col items-center justify-center rounded-br-2xl border border-dashed border-primary-400">
        <IconUpload stroke={1.5} size={32} color="url(#icon-gradient)" />
        <h1 className="mt-4 text-base font-semibold">Drop track here</h1>
        <p className="text-sm text-text-secondary">{description}</p>
      </div>
    </div>
  );
}

export default BackgroundMusicDialog;
